import type {
  AgentConfig,
  AgentLimits,
  Knowledge,
  KnowledgeSection,
  UpdateAgentConfig,
} from "./api";
import { KNOWLEDGE_SECTIONS } from "./api";

/** Los modelos que se sugieren; se puede escribir cualquier otro que el proveedor acepte. */
export const MODELOS_SUGERIDOS: Record<string, { value: string; label: string }[]> = {
  anthropic: [
    { value: "claude-haiku-5-5", label: "claude-haiku-5-5 · el más barato" },
    { value: "claude-sonnet-5-5", label: "claude-sonnet-5-5 · recomendado para notas" },
    { value: "claude-opus-5-5", label: "claude-opus-5-5 · el más capaz" },
  ],
};

/** Lo que edita el formulario. La key se escribe aparte y vacía significa «no tocar». */
export interface FormularioDelAsistente extends AgentLimits {
  enabled: boolean;
  displayName: string;
  provider: string;
  model: string;
  apiKey: string;
  preferredSeller: string | null;
}

const LIMITES: (keyof AgentLimits)[] = [
  "maxToolCallsPerTurn",
  "maxTokensPerDay",
  "maxTurnsPerConversation",
  "ratePerMin",
];

export function formularioDesde(c: AgentConfig | null): FormularioDelAsistente {
  return {
    enabled: c?.enabled ?? false,
    displayName: c?.displayName ?? "",
    provider: c?.provider ?? "anthropic",
    model: c?.model ?? "",
    apiKey: "",
    preferredSeller: c?.preferredSeller ?? null,
    maxToolCallsPerTurn: c?.limits?.maxToolCallsPerTurn ?? null,
    maxTokensPerDay: c?.limits?.maxTokensPerDay ?? null,
    maxTurnsPerConversation: c?.limits?.maxTurnsPerConversation ?? null,
    ratePerMin: c?.limits?.ratePerMin ?? null,
  };
}

const numeroONull = (v: unknown): number | null =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v))
    ? null
    : Number(v);

/**
 * El body del `PUT`: sólo lo que cambió, para no pisar lo que otro admin guardó en el medio. La key
 * va sólo si se escribió una, y un límite vaciado viaja como `null`, que en la API es «el default».
 */
export function cambiosParaGuardar(
  antes: FormularioDelAsistente,
  ahora: FormularioDelAsistente,
): UpdateAgentConfig {
  const out: UpdateAgentConfig = {};
  if (ahora.enabled !== antes.enabled) out.enabled = ahora.enabled;
  const nombre = ahora.displayName.trim();
  if (nombre && nombre !== antes.displayName.trim()) out.displayName = nombre;
  if (ahora.provider && ahora.provider !== antes.provider) out.provider = ahora.provider;
  const modelo = ahora.model.trim();
  if (modelo && modelo !== antes.model.trim()) out.model = modelo;
  const key = ahora.apiKey.trim();
  if (key) out.apiKey = key;
  if ((ahora.preferredSeller ?? null) !== (antes.preferredSeller ?? null))
    out.preferredSeller = ahora.preferredSeller ?? null;
  for (const k of LIMITES) {
    const v = numeroONull(ahora[k]);
    if (v !== numeroONull(antes[k])) out[k] = v;
  }
  return out;
}

/** Qué falta para poder encenderlo: la API lo rechaza igual (`AGENT_CONFIG_INCOMPLETE`). */
export function faltaParaEncender(
  f: FormularioDelAsistente,
  tieneKey: boolean,
): string[] {
  const falta: string[] = [];
  if (!f.provider) falta.push("el proveedor");
  if (!f.model.trim()) falta.push("el modelo");
  if (!tieneKey && !f.apiKey.trim()) falta.push("la API key");
  return falta;
}

/** Las secciones del conocimiento, con el título y la guía que ve quien las escribe. */
export const SECCIONES: Record<KnowledgeSection, { titulo: string; guia: string }> = {
  negocio: {
    titulo: "El negocio",
    guia: "Rubros, zonas a las que llega, horarios, envíos, medios de pago y políticas de la tienda.",
  },
  presupuestar: {
    titulo: "Cómo presupuestar",
    guia: "Qué sugerir para cada obra, equivalencias entre productos, mínimos de venta y marcas que se prefieren.",
  },
  recetas: {
    titulo: "Recetas de cómputo",
    guia: "Cuánto material lleva cada trabajo, por m² o por metro, con el desperdicio y cómo se redondea.",
  },
  faq: {
    titulo: "Preguntas frecuentes",
    guia: "Las preguntas que más hacen los clientes y cómo las contesta la tienda.",
  },
  no_hacer: {
    titulo: "Lo que no tiene que hacer",
    guia: "Lo que el asistente nunca debe prometer, ofrecer ni decir, aunque se lo pidan.",
  },
  tono: {
    titulo: "Tono",
    guia: "Cómo habla la tienda: el trato, el largo de las respuestas y las expresiones que usa o evita.",
  },
};

/**
 * El conocimiento limpio, como lo guarda la API: recortado y sin las secciones vacías. Las notas de
 * sucursal (D58), en orden de código y sin las vacías; sin ninguna, no viaja el campo.
 */
export function normalizarConocimiento(k: Knowledge): Knowledge {
  const out: Knowledge = {};
  for (const s of KNOWLEDGE_SECTIONS) {
    const v = k[s]?.trim();
    if (v) out[s] = v;
  }
  const notas: Record<string, string> = {};
  for (const code of Object.keys(k.branchNotes ?? {}).sort()) {
    const v = k.branchNotes?.[code]?.trim();
    if (v) notas[code] = v;
  }
  if (Object.keys(notas).length) out.branchNotes = notas;
  return out;
}

export function conocimientoCambio(antes: Knowledge, ahora: Knowledge): boolean {
  const a = normalizarConocimiento(antes);
  const b = normalizarConocimiento(ahora);
  return (
    KNOWLEDGE_SECTIONS.some((s) => (a[s] ?? "") !== (b[s] ?? "")) ||
    JSON.stringify(a.branchNotes ?? {}) !== JSON.stringify(b.branchNotes ?? {})
  );
}

/** Las notas de sucursal que pasan el tope (D58): el mismo de una sección. */
export function notasExcedidas(k: Knowledge, tope: number): string[] {
  return Object.entries(k.branchNotes ?? {})
    .filter(([, v]) => (v?.trim().length ?? 0) > tope)
    .map(([code]) => code);
}

/** Las secciones que pasan su tope: con alguna, no se puede guardar. */
export function seccionesExcedidas(
  k: Knowledge,
  topes: { key: KnowledgeSection; maxLength: number }[],
): KnowledgeSection[] {
  return topes
    .filter((t) => (k[t.key]?.trim().length ?? 0) > t.maxLength)
    .map((t) => t.key);
}

/**
 * Precios de lista en US$ por millón de tokens (entrada, salida). El pod todavía no informa el costo
 * de cada turno, así que la pantalla lo estima con esto: es una referencia, no la factura. Haiku 5.5
 * cobra más con prompts de más de 100K tokens, que un turno del asistente no alcanza.
 */
export const PRECIOS_POR_MILLON: Record<string, { entrada: number; salida: number }> = {
  "claude-haiku-5-5": { entrada: 0.1, salida: 0.5 },
  "claude-sonnet-5-5": { entrada: 2, salida: 10 },
  "claude-opus-5-5": { entrada: 4, salida: 20 },
};

/** El costo estimado de esos tokens, o `null` si el modelo no tiene precio cargado. */
export function costoEstimado(
  model: string | null | undefined,
  tokensIn: number,
  tokensOut: number,
): number | null {
  const p = model ? PRECIOS_POR_MILLON[model] : undefined;
  if (!p) return null;
  return (tokensIn * p.entrada + tokensOut * p.salida) / 1_000_000;
}

/** «US$ 1,23»; debajo de un dólar, hasta tres decimales, y debajo de un milésimo, «menos de US$ 0,001». */
export function dolares(n: number): string {
  if (n > 0 && n < 0.001) return "menos de US$ 0,001";
  const decimales = n > 0 && n < 1 ? 3 : 2;
  return `US$ ${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: decimales })}`;
}

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** «2026-10» → «octubre de 2026». */
export function nombreDelMes(mes: string): string {
  const [anio, m] = mes.split("-").map(Number);
  return MESES[m - 1] ? `${MESES[m - 1]} de ${anio}` : mes;
}
